<template>
  <div ref="playground">
    <vue-draggable-resizable
      v-for="viewer in viewers"
      :key="viewer.id"
      :ref="'viewer-' + viewer.id"
      class="draggable"
      :w="viewer.imageWidth"
      :h="viewer.imageHeight"
      :min-width="viewer.minWidth"
      :min-height="viewer.minHeight"
      :resizable="true"
      :style="{ zIndex: viewer.zIndex }"
      @resizing="() => onResizing(viewer)"
      @dragstop="(x, y) => { viewer.x = x; viewer.y = y }"
      @pointerdown.native="bringToFrontById(viewer.id)"
      :parent="true"
      :x="viewer.x"
      :y="viewer.y"
    >
      <div class="header">
        <div class="left-side">
          <button v-if="!viewer.pxPerCm" @click="toggleMeasure1cm(viewer)" class="button is-small" :class="{ active: viewer.isMeasuring1cm }">{{ $t('set-1cm-reference') }}</button>
          <button v-if="viewer.pxPerCm > 0" @click="toggleMeasureLine(viewer)" class="button is-small" :class="{ active: viewer.isMeasuringLine }">{{ $t('measure-length')}}</button>
        </div>

        <div class="center-side">
          <span>{{ viewer.fileName }}</span>
        </div>
        
        <div class="right-side">
          <span class="icon-button" v-if="viewer.pxPerCm" ><i  @click="resetMeasurement(viewer)"  class="fas fa-sync-alt"></i></span>
          <span class="icon-button"><i class="fas fa-save" @click="saveAnnotations(viewer)"></i></span>
          <span class="icon-button"><i class="fas fa-download" @click="downloadImage(viewer)"></i></span>
          <span class="icon-button"><i class="fas fa-times" @click="closeViewer(viewer)"></i></span>
        </div>
      </div>
      <div :id="viewer.mapId" class="map-container"></div>
    </vue-draggable-resizable>
  </div>
</template>

<script>
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import { getCenter } from 'ol/extent';
import ImageLayer from 'ol/layer/Image';
import VectorLayer from 'ol/layer/Vector';
import Projection from 'ol/proj/Projection';
import Static from 'ol/source/ImageStatic';
import VectorSource from 'ol/source/Vector';
import { Draw } from 'ol/interaction';
import { Style, Stroke, Text, Fill } from 'ol/style';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';

import VueDraggableResizable from 'vue-draggable-resizable';
import 'vue-draggable-resizable/dist/VueDraggableResizable.css';
import {appendShortTermToken} from '@/utils/token-utils.js';
import {get} from '@/utils/store-helpers.js';

import { AttachedFileAnnotation } from 'cytomine-client';


export default {
  name: 'attached-image-dialog',
  components: { VueDraggableResizable },
  props: {
    initialViewers: { type: Array, default: () => [] } // 부모로부터 전달받은 이미지 정보
  },
  data() {
    return {
      viewers: [],
      topZIndex: 1,
      localShortTermToken: '',
      viewerSequence: 0,
    };
  },
  computed: {
    shortTermToken: get('currentUser/shortTermToken'),
  },
  watch: {
    initialViewers: {
      handler(newList) {
        newList.forEach(file => this.addViewer(file));
      },
      deep: true,
      immediate: true
    },
    shortTermToken(newVal) {
      if (!this.localShortTermToken && newVal) {
        this.localShortTermToken = newVal;
      }
    },
  },
  methods: {
    async handleResize() {
      this.$nextTick(() => {
        this.viewers.forEach(v => this.clampViewerPosition(v));
      });
    },
    clampViewerPosition(viewer) {
      const parent = this.$refs.playground;
      if (!parent) return;

      const maxX = Math.max(parent.clientWidth - viewer.imageWidth, 0);
      const maxY = Math.max(parent.clientHeight - viewer.imageHeight, 0);

      viewer.x = Math.min(Math.max(viewer.x, 0), maxX);
      viewer.y = Math.min(Math.max(viewer.y, 0), maxY);
    },
    onDragStop(viewer, x, y) {
      viewer.x = x;
      viewer.y = y;
      this.clampViewerPosition(viewer);
    },
    async addViewer(file) {
      if (this.viewers.some(v => v.id === file.id)) return;

      const id = file.id;
      const url = appendShortTermToken(file.fileUrl, this.shortTermToken) || URL.createObjectURL(file.file);
      const img = new Image();
      
      const WIDTH = 800;
      const HEIGHT = 500;
      const MIN_WIDTH = 500;
      const MIN_HEIGHT = 400;

      const START_X = 800;
      const START_Y = 10;
      const STEP_X = 30;
      const STEP_Y = 50;
      const CYCLE_X_OFFSET = 20;
      const CYCLE_Y_OFFSET = 20;

      const parent = this.$refs.playground;

      let x = START_X;
      let y = START_Y;

      if (parent) {
        const maxX = Math.max(parent.clientWidth - WIDTH, 0);
        const maxY = Math.max(parent.clientHeight - HEIGHT, 0);

        // 한 사이클에서 가능한 계단 수
        const maxStepX = Math.floor((maxX - START_X) / STEP_X);
        const maxStepY = Math.floor((maxY - START_Y) / STEP_Y);
        const stepsPerCycle = Math.max(Math.min(maxStepX, maxStepY) + 1, 1);

        const viewerIndex = this.viewerSequence;
        this.viewerSequence++;

        // 나머지로 계단을 다시 0부터
        const stepIndex = viewerIndex % stepsPerCycle;
        const cycleIndex = Math.floor(viewerIndex / stepsPerCycle);

        x = START_X + stepIndex * STEP_X + cycleIndex * CYCLE_X_OFFSET;
        y = START_Y + stepIndex * STEP_Y + cycleIndex * CYCLE_Y_OFFSET;

        // 안전 보정
        x = Math.min(x, maxX);
        y = Math.min(y, maxY);
      }

      img.onload = async () => {
        const viewer = {
          id,
          mapId: `map-${id}`,
          map: null,
          imageLayer: null,
          measure1cmLayer: null,
          lineVectorLayer: null,
          measureInteraction: null,
          fileName: file.fileName || file.name,
          imageWidth: WIDTH,
          imageHeight: HEIGHT,
          originalWidth: img.width,
          originalHeight: img.height,
          pxPerCm: 0,
          isMeasuring1cm: false,
          zIndex: this.topZIndex++,
          fileUrl: file.fileUrl,
          isMeasuringLine: false,
          x,
          y,
          minWidth: MIN_WIDTH,
          minHeight: MIN_HEIGHT,
          hasAnnotations: false,
          // annotId: null,
        };

        this.viewers.push(viewer);
        this.$nextTick(() => this.initViewer(viewer, url));
        
        // annotatoin 정보 불러옴
        const annotationData = await new AttachedFileAnnotation.fetchByAttachedFile(file.id);
        if (annotationData && annotationData.annotation) {
          this.loadAnnotation(viewer, annotationData.annotation, annotationData.pxPerCm);
        }
        else if (annotationData) {
          // annotationData는 있지만 annotation이 없는 경우, 빈 배열로 처리
          this.loadAnnotation(viewer, '[]', annotationData.pxPerCm);
        }
        else {
          // annotationData 자체가 없으면 기본값 처리
          this.loadAnnotation(viewer, '[]', 0);
        }
      };
      img.src = url;
    },
    initViewer(viewer, url) {
      const img = new Image();
      img.onload = () => {
        const extent = [0, 0, img.width, img.height];
        const projection = new Projection({ code: `local-image-${viewer.id}`, units: 'pixels', extent });
        viewer.map = new Map({
          target: viewer.mapId,
          layers: [],
          view: new View({ projection, center: getCenter(extent), zoom: 1, maxZoom: 8 })
        });
        viewer.imageLayer = new ImageLayer({ source: new Static({ url, projection, imageExtent: extent }) });
        viewer.map.addLayer(viewer.imageLayer);
        viewer.map.updateSize();
      };
      img.src = url;
    },
    bringToFrontById(id) {
      const viewer = this.viewers.find(v => v.id === id);
      if (!viewer) return;

      // 현재 viewer를 최상위로 올림
      viewer.zIndex = ++this.topZIndex;

      // topZIndex가 일정 값 이상이면 재정렬
      if (this.topZIndex >= 1000) {
        // 현재 viewer를 제외한 나머지 viewer들의 zIndex 1부터 재할당
        let z = 1;
        this.viewers.forEach(v => {
          if (v !== viewer) {
            v.zIndex = z++;
          }
        });
        // 최종적으로 현재 viewer를 topZIndex로
        viewer.zIndex = z;
        // topZIndex 갱신
        this.topZIndex = z;
      }
    },
    onResizing(viewer) {
      this.$nextTick(() => viewer.map && viewer.map.updateSize());
    },
    closeByFildId(id) { // 외부에서 삭제
      const viewer = this.viewers.find(v => v.id === id);
      if (!viewer) return;

      // interaction 제거
      if (viewer.measureInteraction) {
        viewer.map.removeInteraction(viewer.measureInteraction);
        viewer.measureInteraction = null;
      }

      // 레이어 제거
      viewer.measure1cmLayer && viewer.map.removeLayer(viewer.measure1cmLayer);
      viewer.lineVectorLayer && viewer.map.removeLayer(viewer.lineVectorLayer);
      viewer.imageLayer && viewer.map.removeLayer(viewer.imageLayer);

      // map 타겟 해제
      viewer.map && viewer.map.setTarget(null);

      // viewer 제거
      this.viewers = this.viewers.filter(v => v.id !== viewer.id);

      if (this.viewers.length == 0) this.viewerSequence = 0;
    },
    closeViewer(viewer) { // 내부에서 삭제
      // interaction 제거
      if (viewer.measureInteraction) {
        viewer.map.removeInteraction(viewer.measureInteraction);
        viewer.measureInteraction = null;
      }

      // 레이어 제거
      viewer.measure1cmLayer && viewer.map.removeLayer(viewer.measure1cmLayer);
      viewer.lineVectorLayer && viewer.map.removeLayer(viewer.lineVectorLayer);
      viewer.imageLayer && viewer.map.removeLayer(viewer.imageLayer);

      // map 타겟 해제 (중요)
      viewer.map && viewer.map.setTarget(null);

      // viewer 제거
      this.viewers = this.viewers.filter(v => v.id !== viewer.id);

      if (this.viewers.length == 0) this.viewerSequence = 0;

      // 이벤트 알림
      this.$eventBus.$emit('closeImageWindow', viewer);
    },

    toggleMeasure1cm(viewer) {
      viewer.isMeasuring1cm = !viewer.isMeasuring1cm;
      if (viewer.isMeasuring1cm) this.startMeasure1cm(viewer);
      else this.stopInteraction(viewer);
    },

    startMeasure1cm(viewer) {
      // 기존 interaction 제거
      if (viewer.measureInteraction) {
        viewer.map.removeInteraction(viewer.measureInteraction);
        viewer.measureInteraction = null;
      }

      // 레이어 생성 (없을 때만)
      if (!viewer.measure1cmLayer) {
        viewer.measure1cmLayer = new VectorLayer({
          source: new VectorSource(),
          style: new Style({
            stroke: new Stroke({ color: 'red', width: 2 })
          })
        });

        viewer.map.addLayer(viewer.measure1cmLayer);
      }
      else {
        // 이미 있으면 내용만 정리
        viewer.measure1cmLayer.getSource().clear();
      }

      // Draw interaction 새로 생성
      viewer.measureInteraction = new Draw({
        type: 'LineString',
        maxPoints: 2,
        source: viewer.measure1cmLayer.getSource()
      });

      viewer.measureInteraction.on('drawend', e => {
        const c = e.feature.getGeometry().getCoordinates();
        const dx = c[1][0] - c[0][0];
        const dy = c[1][1] - c[0][1];
        viewer.pxPerCm = Math.sqrt(dx * dx + dy * dy);

        viewer.measure1cmLayer.getSource().clear();
        viewer.isMeasuring1cm = false;

        this.stopInteraction(viewer);
        this.updateLabels(viewer);
      });

      viewer.map.addInteraction(viewer.measureInteraction);
      viewer.hasAnnotations = true;
    },
    toggleMeasureLine(viewer) {
      viewer.isMeasuringLine = !viewer.isMeasuringLine;

      if (viewer.isMeasuringLine) {
        this.startMeasureLine(viewer);
      }
      else {
        this.stopInteraction(viewer);
      }
    },
    startMeasureLine(viewer) {
      // 기존 interaction만 제거
      if (viewer.measureInteraction) {
        viewer.map.removeInteraction(viewer.measureInteraction);
        viewer.measureInteraction = null;
      }

      // 레이어는 한 번만 생성 + 추가
      if (!viewer.lineVectorLayer) {
        viewer.lineVectorLayer = new VectorLayer({
          source: new VectorSource(),
          style: f => {
            const baseFontSize = 14;
            const scale = viewer.isExporting
              ? viewer.originalWidth / viewer.imageWidth
              : 1;

            const fontSize = Math.min(
              24, // 최대
              Math.max(12, baseFontSize * scale) // 최소
            );

            if (f.getGeometry() instanceof Point) {
              return new Style({
                text: new Text({
                  text: f.get('label'),
                  font: `${fontSize}px sans-serif`,
                  fill: new Fill({ color: 'black' }),
                  stroke: new Stroke({ color: 'white', width: 2 })
                })
              });
            }
            return new Style({
              stroke: new Stroke({ color: 'blue', width: 2 })
            });
          }
        });

        viewer.map.addLayer(viewer.lineVectorLayer);
      }

      // Draw interaction은 매번 새로
      viewer.measureInteraction = new Draw({
        type: 'LineString',
        maxPoints: 2,
        source: viewer.lineVectorLayer.getSource()
      });

      viewer.measureInteraction.on('drawend', e => {
        const c = e.feature.getGeometry().getCoordinates();
        const mid = [
          (c[0][0] + c[1][0]) / 2,
          (c[0][1] + c[1][1]) / 2
        ];

        const label = new Feature(new Point(mid));
        label.set('coords', c);
        label.set('label', this.calcLength(c, viewer));

        // 기존 것 유지 + 새 라벨만 추가
        viewer.lineVectorLayer.getSource().addFeature(label);
      });

      viewer.map.addInteraction(viewer.measureInteraction);
      viewer.hasAnnotations = true;
    },

    calcLength(c, viewer) {
      const d = Math.hypot(c[1][0] - c[0][0], c[1][1] - c[0][1]);
      const cm = d / viewer.pxPerCm;
      if (cm >= 1) return cm.toFixed(2) + ' cm';
      const mm = cm * 10;
      if (mm >= 0.1) return mm.toFixed(2) + ' mm';
      return (mm * 1000).toFixed(1) + ' μm';
    },

    updateLabels(viewer) {
      if (!viewer.lineVectorLayer) return;
      viewer.lineVectorLayer.getSource().getFeatures().forEach(f => {
        if (f.get('coords')) {
          f.set('label', this.calcLength(f.get('coords'), viewer));
        }
      });
    },

    stopInteraction(viewer) {
      if (!viewer.measureInteraction) return;
      viewer.map.removeInteraction(viewer.measureInteraction);
      viewer.measureInteraction = null;
      viewer.isMeasuringLine = false;
    },

    resetMeasurement(viewer) {
      viewer.pxPerCm = 0;
      viewer.lineVectorLayer?.getSource().clear();
      viewer.measure1cmLayer?.getSource().clear();
      viewer.hasAnnotations = false;
      this.stopInteraction(viewer);
    },
    downloadImage(viewer) {
      const layer = viewer.lineVectorLayer;

      // 레이어가 없으면 원본 다운로드
      if (!layer) {
        window.location.assign(
          appendShortTermToken(viewer.fileUrl, this.shortTermToken)
        );
        return;
      }
      
      // LineString이 없으면 원본 다운로드
      const features = layer.getSource().getFeatures();
      const hasLine = features.some(f => f.getGeometry() instanceof LineString);
      if (!hasLine) {
        window.location.assign(
          appendShortTermToken(viewer.fileUrl, this.shortTermToken)
        );
        return;
      }
      
      // 측정 상태의 interaction 제거
      const map = viewer.map;
      if (viewer.measureInteraction) {
        map.removeInteraction(viewer.measureInteraction);
        viewer.measureInteraction = null;
      }
      viewer.isMeasuring1cm = false;
      viewer.isMeasuringLine = false;

      const extent = viewer.imageLayer.getSource().getImageExtent();
      const width = extent[2] - extent[0];
      const height = extent[3] - extent[1];

      // 저장용 폰트 크기 계산
      const exportFontSize = this.calcExportFontSize(viewer, width, height);

      // 기존 스타일 백업
      const originalStyle = layer.getStyle();

      // 라벨 좌표 보정 (텍스트 실제 폭 기준)
      const labelBackup = [];
      const measureCanvas = document.createElement('canvas');
      const measureCtx = measureCanvas.getContext('2d');
      measureCtx.font = `${exportFontSize}px sans-serif`;

      layer.getSource().getFeatures().forEach(f => {
        if (f.getGeometry() instanceof Point && f.get('label')) {
          const geom = f.getGeometry();
          const originalCoord = geom.getCoordinates().slice();
          const text = f.get('label');

          labelBackup.push({
            feature: f,
            coord: originalCoord
          });

          // 텍스트 실제 픽셀 크기
          const textWidth = measureCtx.measureText(text).width;
          const halfTextWidth = textWidth / 2;
          const halfTextHeight = exportFontSize / 2;

          const minX = extent[0] + halfTextWidth;
          const maxX = extent[2] - halfTextWidth;
          const minY = extent[1] + halfTextHeight;
          const maxY = extent[3] - halfTextHeight;

          const clamped = [
            Math.min(Math.max(originalCoord[0], minX), maxX),
            Math.min(Math.max(originalCoord[1], minY), maxY)
          ];

          geom.setCoordinates(clamped);
        }
      });

      // 저장 전용 스타일 적용
      layer.setStyle(f => {
        if (f.getGeometry() instanceof Point && f.get('label')) {
          return new Style({
            text: new Text({
              text: f.get('label'),
              font: `${exportFontSize}px sans-serif`,
              textAlign: 'center',
              textBaseline: 'middle',
              fill: new Fill({ color: 'black' }),
              stroke: new Stroke({
                color: 'white',
                width: exportFontSize * 0.15
              })
            })
          });
        }
        return new Style({
          stroke: new Stroke({ color: 'blue', width: 2 })
        });
      });

      // 캔버스 렌더링
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      const originalSize = map.getSize();
      map.setSize([width, height]);
      map.getView().fit(extent, {
        size: [width, height],
        constrainResolution: false
      });
      map.renderSync();

      map.getViewport().querySelectorAll('canvas').forEach(layerCanvas => {
        ctx.drawImage(layerCanvas, 0, 0, width, height);
      });

      // map 상태 복원
      map.setSize(originalSize);
      map.getView().fit(extent, {
        size: originalSize,
        constrainResolution: false
      });
      map.renderSync();

      // 저장 + 복구
      canvas.toBlob(blob => {
        // 스타일 복구
        layer.setStyle(originalStyle);

        // 라벨 좌표 복구
        labelBackup.forEach(({ feature, coord }) => {
          feature.getGeometry().setCoordinates(coord);
        });

        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download =
          viewer.fileName.replace(/\.[^/.]+$/, '') + '_measured.png';
        a.click();
      }, 'image/png');
    },
    calcExportFontSize(viewer, width, height) {
      const screenFontSize = 12;

      // 화면에서 실제로 보이는 이미지 크기
      const screenWidth = viewer.imageWidth;
      const screenHeight = viewer.imageHeight;

      // 저장될 원본 이미지 크기
      const exportWidth = width;
      const exportHeight = height;

      // 화면 / 저장 각각에서 긴 변 선택
      const screenLong = Math.max(screenWidth, screenHeight);
      const exportLong = Math.max(exportWidth, exportHeight);

      // 긴 변 기준 스케일
      const scale = exportLong / screenLong;

      return Math.max(screenFontSize, screenFontSize * scale);
    },
    async saveAnnotations(viewer) {
      if (!viewer) return;

      const annotations = [];

      // 선과 라벨(feature) 수집
      if (viewer.lineVectorLayer) {
        const features = viewer.lineVectorLayer.getSource().getFeatures();
        features.forEach(f => {
          const geom = f.getGeometry();
          const type = geom instanceof Point ? 'point' : 'line';
          const coords = geom.getCoordinates();

          // line이면 coords 그대로, point이면 좌표 하나만
          const annotation = {
            type,
            coordinates: coords,
            label: f.get('label') || null
          };
          annotations.push(annotation);
        });
      }

      // 1cm 측정선 정보
      if (viewer.measure1cmLayer) {
        const measureFeatures = viewer.measure1cmLayer.getSource().getFeatures();
        measureFeatures.forEach(f => {
          const geom = f.getGeometry();
          annotations.push({
            type: 'measure1cm',
            coordinates: geom.getCoordinates(),
            // label: '1cm reference'
            label: f.get('label') || null
          });
        });
      }

      // annotation 정보 저장
      try {
        const annot = new AttachedFileAnnotation();
        annot.attachedFile = viewer.id;
        annot.annotation = JSON.stringify(annotations);
        annot.pxPerCm = viewer.pxPerCm;
        await annot.save();

        this.$notify({type: 'success', text: this.$t('notif-success-attached-file-measured-info-creation')});
      }
      catch (error) {
        console.error(error);
        this.$notify({type: 'error', text: this.$t('notif-error-attached-file-measured-info-creation')});
      }
    },
    loadAnnotation(viewer, annotation, pxPerCm) {
      const annots = JSON.parse(annotation);
      if (annots.length === 0) return;
      
      // 기존 내용 초기화
      this.resetMeasurement(viewer);

      viewer.pxPerCm = pxPerCm;
      viewer.hasAnnotations = annotation.length > 0;

      if (!viewer.lineVectorLayer) {
        viewer.lineVectorLayer = new VectorLayer({
          source: new VectorSource(),
          style: f => {
            const type = f.get('type');

            if (f.get('label')) {
              return new Style({
                text: new Text({
                  text: f.get('label'),
                  font: '14px sans-serif',
                  fill: new Fill({ color: 'black' }),
                  stroke: new Stroke({ color: 'white', width: 2 })
                })
              });
            }
                  
            // 1cm 기준선
            if (type === 'measure1cm') {
              return new Style({
                stroke: new Stroke({
                  color: 'red',
                  width: 2
                })
              });
            }

            return new Style({
              stroke: new Stroke({ color: 'blue', width: 2 })
            });
          }
        });
        viewer.map.addLayer(viewer.lineVectorLayer);
      }

      viewer.lineVectorLayer.getSource().clear();

      annots.forEach(a => {
        if (a.type === 'line') {
          const feature = new Feature({ geometry: new LineString(a.coordinates) });
          viewer.lineVectorLayer.getSource().addFeature(feature);
        }
        else if (a.type === 'measure1cm') {
          if (!viewer.measure1cmLayer) {
            viewer.measure1cmLayer = new VectorLayer({
              source: new VectorSource(),
              style: new Style({ stroke: new Stroke({ color: 'red', width: 2 }) })
            });
            viewer.map.addLayer(viewer.measure1cmLayer);
          }
          const feature = new Feature({ geometry: new LineString(a.coordinates) });
          feature.set('label', a.label);
          viewer.measure1cmLayer.getSource().addFeature(feature);
        }
        else if (a.type === 'point') {
          const feature = new Feature({
            geometry: new Point(a.coordinates)
          });
          feature.set('label', a.label);
          viewer.lineVectorLayer.getSource().addFeature(feature);
        }
      });

      viewer.map.renderSync();
    }
  },
  created() {
    this.localShortTermToken = this.shortTermToken;
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.$nextTick(() => this.handleResize()); // 초기 위치 보정
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style scoped>
.draggable { 
  border: 1px solid #ccc; 
  background: #fafafa; 
}

.header { 
  height: 30px; 
  background: #e0e0e0; 
  display: flex; 
  /* justify-content: space-between; */
  align-items: center; 
  gap: 8px; 
  padding: 0 8px; 
}
.left-side {
  width: 10rem;
}
.center-side {
  display: flex; 
  flex: 1;
  min-width: 0;
}
.center-side > * {
  max-width: 100%; 
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.right-side {
  display: flex;
  justify-content: flex-end;
}
.icon-button {
  margin-left: 10px;
  cursor: pointer;
}

.map-container { 
  width: 100%; 
  height: calc(100% - 30px); 
}
button.active { 
  background: #009BDB; 
  color: white; 
}

.overlay-dialog {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none; /* 배경 부모는 상호작용 가능 */
}

.overlay-dialog .draggable {
  pointer-events: auto; /* 드래그 영역만 이벤트 허용 */
}
</style>
